import React, { useState, useEffect } from 'react';
import styles from './DepartmentsLanding.module.css';
import { departments } from '../config/departments';
import type { DepartmentConfig } from '../config/departments';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import JoinDepartmentModal from '../components/JoinDepartmentModal';
import { Users, Target } from 'lucide-react';
import ContextualHelp from '../components/ContextualHelp';

interface User {
  id: number;
  username: string;
  avatarUrl?: string;
  primaryPosition?: string | null;
  secondaryPosition?: string | null;
  tertiaryPosition?: string | null;
}

interface Position {
  title: string;
  description: string;
  rank: number;
  maxHolders?: number | null;
}

interface PositionHolder {
  positionTitle: string;
  positionRank: number;
  maxHolders?: number | null;
  currentCount: number;
}

const getFullAvatarUrl = (avatarUrl: string | null | undefined): string | null => {
  if (!avatarUrl) return null;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
};

function getExpectedChiefTitle(deptName: string): string {
  return `Chief ${deptName} Officer`;
}

const DepartmentsLanding: React.FC = () => {
  const { user } = useAuth();
  const [selectedDept, setSelectedDept] = useState<DepartmentConfig | null>(null);
  const [executive, setExecutive] = useState<User | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [positionHolders, setPositionHolders] = useState<PositionHolder[]>([]);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedDepartmentForJoin, setSelectedDepartmentForJoin] = useState<string | null>(null);

  // Fetch member counts for all departments on mount
  useEffect(() => {
    const fetchMemberCounts = async () => {
      const counts: Record<string, number> = {};
      
      for (const dept of departments) {
        try {
          const res = await axios.get(`/departments/${dept.id}/roster`);
          counts[dept.id] = res.data.length;
        } catch (err) {
          console.error(`Failed to fetch member count for ${dept.id}:`, err);
          counts[dept.id] = 0;
        }
      }
      
      setMemberCounts(counts);
    };

    fetchMemberCounts();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDept) return;
      
      setLoading(true);
      try {
        const [rosterRes, positionsRes] = await Promise.all([
          axios.get(`/departments/${selectedDept.id}/roster`),
          axios.get(`/Positions/department/${selectedDept.id}`),
        ]);

        const users: User[] = rosterRes.data;
        const exec = users.find((u) => 
          u.primaryPosition?.startsWith("Chief ") ||
          u.secondaryPosition?.startsWith("Chief ") ||
          u.tertiaryPosition?.startsWith("Chief ")
        ) || null;
        
        setExecutive(exec);
        setPositions(positionsRes.data);

        // Build position holders list (non-Chief positions with assigned users)
        const holders: PositionHolder[] = [];
        const positionMap = new Map<string, User[]>();

        // Group users by their position in this department
        users.forEach(user => {
          const deptPosition = 
            user.primaryPosition || 
            user.secondaryPosition || 
            user.tertiaryPosition;
          
          if (deptPosition && !deptPosition.startsWith("Chief ")) {
            if (!positionMap.has(deptPosition)) {
              positionMap.set(deptPosition, []);
            }
            positionMap.get(deptPosition)!.push(user);
          }
        });

        // Convert to position holders array with counts, using rank from backend
        positionsRes.data.forEach((pos: Position) => {
          if (!pos.title.startsWith("Chief ")) {
            const usersInPosition = positionMap.get(pos.title) || [];
            holders.push({
              positionTitle: pos.title,
              positionRank: pos.rank || 999,
              maxHolders: pos.maxHolders,
              currentCount: usersInPosition.length
            });
          }
        });

        // Sort by rank (already ordered from backend, but ensure it)
        holders.sort((a, b) => a.positionRank - b.positionRank);
        setPositionHolders(holders);
        
      } catch (err) {
        console.error('Failed to fetch department data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDept]);

  const handleJoinDepartment = async (deptId: string) => {
    setSelectedDepartmentForJoin(deptId);
    setShowJoinModal(true);
    setSelectedDept(null); // Close the department detail modal
  };

  const isUserInDepartment = (deptId: string): boolean => {
    if (!user) return false;
    return (
      user.primaryPosition?.department === deptId ||
      user.secondaryPosition?.department === deptId ||
      user.tertiaryPosition?.department === deptId
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <h1 className={styles.pageTitle}>Find Your Place in TFIC</h1>
          <ContextualHelp featureArea="departments" />
        </div>
        <p className={styles.pageDescription}>
          Join a department that matches your skills and interests. Most operational roles are in Operations, 
          while specialized positions are distributed across other departments. Make your mark and help shape the future of TFIC.
        </p>

        <div className={styles.cardGrid}>
          {departments.map((dept) => (
            <div
              key={dept.id}
              className={styles.card}
              style={{ ['--dept-color' as any]: dept.color }}
            >
              {dept.bannerImage && (
                <div className={styles.cardBanner}>
                  <img
                    src={dept.bannerImage}
                    alt={`${dept.name} banner`}
                    className={styles.cardBannerImg}
                  />
                </div>
              )}
              <div className={styles.cardTitle}>
                {dept.icon} {dept.name}
              </div>
              <div className={styles.cardDescription}>{dept.description}</div>
              
              <div className={styles.cardStats}>
                <div className={styles.statItem}>
                  <Users size={16} className={styles.statIcon} />
                  <span className={styles.statValue}>{memberCounts[dept.id] || 0}</span>
                  <span className={styles.statLabel}>members</span>
                </div>
              </div>

              {dept.id === 'operations' && (
                <div className={styles.opportunityBadge}>
                  <Target size={14} className={styles.badgeIcon} />
                  <span className={styles.badgeText}>Most positions available here</span>
                </div>
              )}

              <div className={styles.cardActions}>
                <button 
                  className={styles.viewDetailsBtn}
                  onClick={() => setSelectedDept(dept)}
                >
                  View Details
                </button>
                {user && !isUserInDepartment(dept.id) && (
                  <button 
                    className={styles.joinBtn}
                    onClick={() => handleJoinDepartment(dept.id)}
                  >
                    Join
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedDept && (
        <div className={styles.modalOverlay} onClick={() => setSelectedDept(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            {selectedDept.bannerImage && (
              <img
                src={selectedDept.bannerImage}
                alt={`${selectedDept.name} banner`}
                className={styles.modalBanner}
              />
            )}
            <h2 className={styles.modalTitle}>
              {selectedDept.icon} {selectedDept.name}
            </h2>
            <p className={styles.modalDescription}>{selectedDept.description}</p>
            
            {positionHolders.filter(h => !h.maxHolders || h.currentCount < h.maxHolders).length > 0 && (
              <div className={styles.opportunityCallout}>
                <strong>{positionHolders.filter(h => !h.maxHolders || h.currentCount < h.maxHolders).length} open position{positionHolders.filter(h => !h.maxHolders || h.currentCount < h.maxHolders).length !== 1 ? 's' : ''}</strong> waiting to be filled. 
                Your skills could make a real difference here.
              </div>
            )}

            {/* Leadership Section */}
            <div className={styles.leadershipSection}>
              <h3 className={styles.sectionTitle}>Leadership</h3>
              {executive ? (
                <div className={styles.executiveCard}>
                  <img
                    src={getFullAvatarUrl(executive.avatarUrl) || 'https://storage.tficorg.org/DefaultIcon.png'}
                    alt={executive.username}
                    className={styles.executiveAvatar}
                  />
                  <div className={styles.executiveInfo}>
                    <div className={styles.executivePosition}>
                      {executive.primaryPosition?.startsWith("Chief ") ? executive.primaryPosition
                        : executive.secondaryPosition?.startsWith("Chief ") ? executive.secondaryPosition
                        : executive.tertiaryPosition?.startsWith("Chief ") ? executive.tertiaryPosition
                        : getExpectedChiefTitle(selectedDept.name)}
                    </div>
                    <div className={styles.executiveName}>{executive.username}</div>
                  </div>
                </div>
              ) : (
                <div className={styles.noLeader}>
                  <span>{getExpectedChiefTitle(selectedDept.name)}: TBD</span>
                </div>
              )}
            </div>

            {/* Positions Section */}
            {loading ? (
              <div className={styles.loadingSection}>Loading positions...</div>
            ) : positionHolders.length > 0 ? (
              <div className={styles.positionsSection}>
                <h3 className={styles.sectionTitle}>Available Roles & Current Team</h3>
                <div className={styles.positionsList}>
                  {positionHolders.map((holder, index) => {
                    const hasOpenings = !holder.maxHolders || holder.currentCount < holder.maxHolders;
                    const isUnlimited = !holder.maxHolders || holder.maxHolders === 0;
                    
                    return (
                      <div key={`${holder.positionTitle}-${index}`} className={styles.positionItem}>
                        <div className={styles.positionTitle}>{holder.positionTitle}</div>
                        <div className={styles.positionStats}>
                          <span className={styles.positionCount}>
                            {isUnlimited ? (
                              <>{holder.currentCount} member{holder.currentCount !== 1 ? 's' : ''}</>
                            ) : (
                              <>{holder.currentCount}/{holder.maxHolders} filled</>
                            )}
                          </span>
                          {hasOpenings && (
                            <span className={styles.openBadge}>OPEN</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={styles.noPositions}>
                No positions defined for this department yet.
              </div>
            )}

            {/* Stats */}
            <div className={styles.statsSection}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total Members:</span>
                <span className={styles.statValue}>{memberCounts[selectedDept.id] || 0}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Open Positions:</span>
                <span className={styles.statValue}>
                  {positionHolders.filter(h => !h.maxHolders || h.currentCount < h.maxHolders).length}
                </span>
              </div>
            </div>

            <div className={styles.modalButtons}>
              {user && !isUserInDepartment(selectedDept.id) && (
                <button 
                  className={styles.modalJoinBtn}
                  onClick={() => handleJoinDepartment(selectedDept.id)}
                >
                  Join Department
                </button>
              )}
              <button 
                className={styles.modalCloseBtn}
                onClick={() => setSelectedDept(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <JoinDepartmentModal
        isOpen={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setSelectedDepartmentForJoin(null);
        }}
        onSuccess={() => {
          // Optionally refresh data after successful application
          console.log('Application submitted successfully');
        }}
        preselectedDepartment={selectedDepartmentForJoin}
      />
    </div>
  );
};

export default DepartmentsLanding;